import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  WorkOutline as WorkIcon,
  AutoAwesome as AiSummaryIcon,
} from '@mui/icons-material';
import type { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { type Dayjs } from 'dayjs';
import { DateRangeSelector } from '@/common-components/date-range-selector';
import ParityBar from '@/common-components/parity-bar/parity-bar';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { PERMISSIONS } from '@/common-constants/permissions';
import { jobService } from '@/services/api';
import type { JobItem, ListJobsRequest } from '@/common-types';

// ── Constants ──────────────────────────────────────────────────────────

const REGIONS = [
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
];

const JOB_TYPES = [
  { value: 'adhoc' as const, label: 'Adhoc' },
  { value: 'schedule' as const, label: 'Scheduled' },
];

const PAGE_SIZE = 10;

const ACTION_DISPLAY_NAMES: Record<string, string> = {
  ember_adhoc_dr_selective_sync: 'Adhoc Selective Sync',
  ember_schedule_dr_selective_sync: 'Scheduled Selective Sync',
  ember_adhoc_dr_sync: 'Adhoc DR Sync (Legacy)',
  ember_schedule_dr_sync: 'Scheduled DR Sync (Legacy)',
};

// ── Helpers ────────────────────────────────────────────────────────────

function formatActionName(action?: string): string {
  if (!action) return '—';
  if (ACTION_DISPLAY_NAMES[action]) return ACTION_DISPLAY_NAMES[action];
  return action
    .replace(/^ember_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTimestamp(ts?: string): string {
  if (!ts) return '—';
  const d = dayjs(ts);
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : ts;
}


/** Default: last 30 days, end = today */
function getDefaultDateRange(): DateRange<Dayjs> {
  return [dayjs().subtract(30, 'day'), dayjs().endOf('day')];
}

function getOrgName(
  job: JobItem,
  key: 'sourceOrg' | 'destinationOrg'
): { name: string; region: string } | null {
  const org = job.exportOrgs?.[key];
  if (!org || typeof org !== 'object') return null;
  const o = org as { name?: string; region?: string };
  return { name: o.name ?? '—', region: o.region ?? '' };
}

// ── Component ──────────────────────────────────────────────────────────

export default function LogOracleJobs() {
  const navigate = useNavigate();
  const { hasPermission } = useAuthentication();
  const canAccess = hasPermission(PERMISSIONS.JOBS_IN_LOG_ORACLE);
  const canAccessAiChat = hasPermission(PERMISSIONS.AI_CHAT_IN_LOG_ORACLE);

  // Filter state
  const [region, setRegion] = useState(REGIONS[0].value);
  const [jobType, setJobType] = useState<'adhoc' | 'schedule'>(JOB_TYPES[0].value);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getDefaultDateRange);

  // Pagination: page is 0-based; pages[page] holds { items, nextCursor } for that page
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<Array<{ items: JobItem[]; nextCursor: Record<string, unknown> | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Search filter on current page jobs
  const [filterText, setFilterText] = useState('');

  const currentPageData = pages[page];
  const currentPageItems = useMemo(
    () => pages[page]?.items ?? [],
    [pages, page]
  );
  const nextCursor = currentPageData?.nextCursor ?? null;
  const hasNextPage = !!nextCursor;

  const filteredJobs = useMemo(() => {
    if (!filterText) return currentPageItems;
    const q = filterText.toLowerCase();
    return currentPageItems.filter(
      (j) =>
        j.transactionId?.toLowerCase().includes(q) ||
        j.initiatedBy?.toLowerCase().includes(q) ||
        formatActionName(j.action).toLowerCase().includes(q) ||
        j.status?.toLowerCase().includes(q)
    );
  }, [currentPageItems, filterText]);

  // Fetch a specific page (0-based). Pass cursor for page > 0 (from previous page's nextCursor).
  const fetchPage = useCallback(
    async (pageIndex: number, cursorForPage: Record<string, unknown> | null = null) => {
      const [start, end] = dateRange;
      if (!start || !end) return;

      const isFirstPage = pageIndex === 0;

      setError(null);
      if (isFirstPage) {
        setLoading(true);
        setPages([]);
        setSearched(true);
      } else {
        setPageLoading(true);
      }

      try {
        const params: ListJobsRequest = {
          region,
          type: jobType,
          startDate: start.startOf('day').toISOString(),
          endDate: end.endOf('day').toISOString(),
          limit: PAGE_SIZE,
          ...(cursorForPage ? { cursor: cursorForPage } : {}),
        };
        const result = await jobService.listJobs(params);

        setPages((prev) => {
          const next = [...prev];
          next[pageIndex] = { items: result.items, nextCursor: result.nextCursor };
          return next;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    },
    [region, jobType, dateRange]
  );

  const handleSearch = () => {
    setPage(0);
    fetchPage(0, null);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    const prevCursor = newPage === 0 ? null : pages[newPage - 1]?.nextCursor ?? null;
    setPage(newPage);
    if (!pages[newPage]) {
      fetchPage(newPage, prevCursor);
    }
  };

  const handleRefresh = () => {
    setPage(0);
    setPages([]);
    fetchPage(0, null);
  };

  // By default fetch first 10 jobs on mount
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!initialFetchDone.current && dateRange[0] && dateRange[1]) {
      initialFetchDone.current = true;
      fetchPage(0, null);
    }
  }, [dateRange, fetchPage]);

  const handleCopyTxnId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      // ignore
    }
  };

  const handleOpenTransaction = (txnId: string) => {
    navigate(`/log-oracle/transaction-lookup?it=${encodeURIComponent(txnId)}`);
  };

  const handleAiSummary = (job: JobItem) => {
    navigate('/log-oracle/ai-chat?jobSummary=1', {
      state: {
        jobSummaryTransactionId: job.transactionId,
        jobSummaryRegion: region,
      },
    });
  };

  // ── Permission guard ──

  if (!canAccess) {
    return (
      <Alert severity="warning">
        You need Log Oracle permission to access this page.
      </Alert>
    );
  }

  // ── Render ──

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pb: 4 }}>
      {/* ── Filters ── */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <CardContent sx={{ py: 2.5, px: 2.5 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'flex-end' }}
            >
              <TextField
                select
                label="Region"
                size="small"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {REGIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Type"
                size="small"
                value={jobType}
                onChange={(e) =>
                  setJobType(e.target.value as 'adhoc' | 'schedule')
                }
                sx={{ minWidth: 140 }}
              >
                {JOB_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>

              <DateRangeSelector
                value={dateRange}
                onChange={(newRange) => setDateRange(newRange)}
                maxDate={dayjs()}
              />

              <Button
                variant="contained"
                size="medium"
                onClick={handleSearch}
                disabled={loading || !dateRange[0] || !dateRange[1]}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  borderRadius: 1.25,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  boxShadow: (t) =>
                    `0 1px 4px ${alpha(t.palette.primary.main, 0.3)}`,
                }}
              >
                {loading ? 'Searching…' : 'Search Jobs'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Error ── */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* ── Results ── */}
      {searched && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          {/* Results header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Jobs
              </Typography>
              <Chip
                label={filteredJobs.length}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Filter results…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{
                  width: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.25,
                    fontSize: '0.8125rem',
                  },
                }}
              />
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Loading indicator */}
          {(loading || pageLoading) && <LinearProgress />}

          {/* Table */}
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Action
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Sync Parity
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Source → Destination
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Date Started
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Date Finished
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Transaction ID
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    Initiated By
                  </TableCell>
                  {canAccessAiChat && (
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      AI
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(loading || pageLoading) && currentPageItems.length === 0 ? (
                  Array.from({ length: PAGE_SIZE }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      <TableCell><Skeleton width={140} /></TableCell>
                      <TableCell><Skeleton width={180} /></TableCell>
                      <TableCell><Skeleton width={200} /></TableCell>
                      <TableCell><Skeleton width={140} /></TableCell>
                      <TableCell><Skeleton width={140} /></TableCell>
                      <TableCell><Skeleton width={260} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      {canAccessAiChat && <TableCell><Skeleton width={32} /></TableCell>}
                    </TableRow>
                  ))
                ) : filteredJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canAccessAiChat ? 8 : 7}>
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <WorkIcon
                          sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          {filterText
                            ? 'No jobs match your filter'
                            : 'No jobs found for the selected criteria'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Try adjusting the region, type, or date range.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredJobs.map((job, idx) => {
                    const source = getOrgName(job, 'sourceOrg');
                    const dest = getOrgName(job, 'destinationOrg');

                    return (
                      <TableRow
                        key={job.jobId || job.transactionId || idx}
                        hover
                        sx={{
                          ...(job.status === 'started' && {
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3,
                              bgcolor: 'info.main',
                              borderRadius: '0 2px 2px 0',
                            },
                          }),
                        }}
                      >
                        {/* Action */}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
                          >
                            {formatActionName(job.action)}
                          </Typography>
                        </TableCell>

                        {/* Sync Parity */}
                        <TableCell sx={{ minWidth: 200 }}>
                          {!job.totalResourcesOnSourceOrg &&
                           !job.totalMatchingResourcesOnDestOrg &&
                           job.status !== 'started' ? (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ fontSize: '0.8125rem' }}
                            >
                              Not Available
                            </Typography>
                          ) : (
                            <ParityBar
                              copied={job.totalMatchingResourcesOnDestOrg || 0}
                              total={job.totalResourcesOnSourceOrg || 1}
                              totalBlocks={10}
                              isRunning={job.status === 'started'}
                              showLoaderWhenRunning={false}
                            />
                          )}
                        </TableCell>

                        {/* Source → Destination */}
                        <TableCell>
                          {source && dest ? (
                            <Stack spacing={0.25}>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                              >
                                {source.name}{source.region ? ` (${source.region})` : ''}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                → {dest.name}{dest.region ? ` (${dest.region})` : ''}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ fontSize: '0.8125rem' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Date Started */}
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            fontSize: '0.8125rem',
                            color: 'text.secondary',
                          }}
                        >
                          {formatTimestamp(job.dateStarted)}
                        </TableCell>

                        {/* Date Finished */}
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            fontSize: '0.8125rem',
                            color: 'text.secondary',
                          }}
                        >
                          {formatTimestamp(job.dateFinished)}
                        </TableCell>

                        {/* Transaction ID */}
                        <TableCell>
                          {job.transactionId ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  bgcolor: 'grey.50',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  maxWidth: 260,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {job.transactionId}
                              </Typography>
                              <Tooltip title="Copy Transaction ID">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyTxnId(job.transactionId!)
                                  }
                                >
                                  <CopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View in Transaction Lookup">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenTransaction(job.transactionId!)
                                  }
                                >
                                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ fontSize: '0.8125rem' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Initiated By */}
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {job.initiatedBy || '—'}
                        </TableCell>

                        {/* AI Summary */}
                        {canAccessAiChat && (
                          <TableCell>
                            <Tooltip title="AI Summary">
                              <IconButton
                                size="small"
                                onClick={() => handleAiSummary(job)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': { bgcolor: 'primary.50' },
                                }}
                              >
                                <AiSummaryIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {searched && (currentPageItems.length > 0 || pages.length > 0) && (
            <TablePagination
              component="div"
              count={
                hasNextPage
                  ? (page + 2) * PAGE_SIZE
                  : page * PAGE_SIZE + currentPageItems.length
              }
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                count > (page + 1) * PAGE_SIZE
                  ? `${from}-${to} of ${count}+`
                  : `${from}-${to} of ${count}`
              }
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '.MuiTablePagination-toolbar': { minHeight: 48 },
              }}
            />
          )}
        </Card>
      )}
    </Box>
  );
}
